import React, { useEffect, useMemo, useState } from 'react';

import { getRoles, getRole } from '@vlabs/api/artemy/client';
import type { RoleId } from '@vlabs/api/artemy/model';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import type { SelectProps } from '@vlabs/uikit/components/controls/select/Select';
import type { TOption } from '@vlabs/uikit/types';

import { getSelectMenuPortalTarget, selectMenuPortalStyles } from '../select-portal';

interface RoleSelectProps extends Omit<SelectProps, 'isAsync' | 'loadOptions' | 'onChange' | 'options' | 'value'> {
  excludeRoleIds?: RoleId[];
  value?: RoleId;
  onChange: (value?: RoleId) => void;
}

function RoleSelect({
  excludeRoleIds = undefined,
  value = undefined,
  onChange,
  styles: stylesProp = undefined,
  ...props
}: RoleSelectProps): React.JSX.Element {
  const [selectedOption, setSelectedOption] = useState<TOption | null>(null);
  const excludedRoleIds = useMemo(() => new Set(excludeRoleIds), [excludeRoleIds]);

  const styles = {
    ...stylesProp,
    menuPortal: (base: object) => ({
      ...selectMenuPortalStyles.menuPortal(base),
      ...(stylesProp?.menuPortal?.(base, {} as never) ?? {}),
    }),
  };

  useEffect(() => {
    if (!value || excludedRoleIds.has(value)) {
      setSelectedOption(null);
      return;
    }

    getRole(value).then((role) => {
      setSelectedOption({
        value: role.role_id,
        label: (role.name ?? role.role_id),
      });
    });
  }, [excludedRoleIds, value]);

  return (
    <Select
      cacheOptions
      defaultOptions
      id="roleSelect"
      isAsync
      isClearable
      loadOptions={async (inputValue) => {
        const rolePromises = await Promise.all([
          getRoles({ name: inputValue }),
          getRoles({ roles_ids: [inputValue] }),
        ]);

        const result = rolePromises.flatMap((role) => role.roles)
          .filter((role) => !!role?.role_id && !excludedRoleIds.has(role.role_id))
          .map((role) => ({
            value: role.role_id,
            label: role.name ?? role.role_id,
          }));

        // Убираем дубликаты
        return [...new Map(result.map(((option) => [option.value, option]))).values()];
      }}
      menuPortalTarget={getSelectMenuPortalTarget()}
      name="roleSelect"
      onChange={(newValue: unknown) => {
        setSelectedOption(newValue as TOption | null);
        onChange((newValue as TOption | null)?.value);
      }}
      styles={styles}
      value={selectedOption ?? undefined}
      {...props}
    />
  );
}

export { RoleSelect };
export type { RoleSelectProps };
